import React from "react";
import { useLocation, useHistory } from "react-router-dom";

function getFilterInternalValue(searchParams, filter, filterDefinition) {
	const toInternalValue = filterDefinition.toInternalValue ?? (v => v);
	const representation = searchParams.get(filter);
	if (representation) {
		if (filterDefinition.default && representation === "*") {
			return toInternalValue(null);
		} else if (filterDefinition.allowed) {
			if (filterDefinition.allowed.indexOf(representation) >= 0) {
				return toInternalValue(representation);
			} else {
				return toInternalValue(filterDefinition.default);
			}
		} else {
			return toInternalValue(representation);
		}
	} else {
		return toInternalValue(filterDefinition.default);
	}
}

function updateFilterRepresentation(
	searchParams,
	filter,
	filterDefinition,
	value
) {
	if (filterDefinition.default && !value) {
		// TODO something's not right about the default value handling, how is it supposed to interacted with toRepresentation / toInternalValue ? is "default" the "default value representation" or the internal value. getFilterInternalValue think it's the representation (filterDefinition.default is passed to `toInternalValue`) but updateFilterRepresentation think it's the internal value (it's compared with `===` to the internal value)
		// it does not matter as long a we only use default for filters with a 1:1 mapping between representation and internal value, but as-is we *will* run into problems if we combine `default` and `toRepresentation` / `toInternalValue`
		searchParams.set(filter, "*");
	} else if (filterDefinition.default && value === filterDefinition.default) {
		searchParams.delete(filter);
	} else {
		const representation = filterDefinition.toRepresentation
			? filterDefinition.toRepresentation(value)
			: value;
		if (representation) {
			searchParams.set(filter, representation);
		} else {
			searchParams.delete(filter);
		}
	}
}

/**
 * Hook to sync the filters (or any set of parameter really) between a component and the page url
 * useQueryParamsFilters takes a filters definition (mapping of filter name to filter config) and returns
 * { filters: { [filter1Name]: filter1Value, [filter2Name]: filter2Value, … }, setFilter(filterName, value) }
 * The filters names are also the query parameters names.
 *
 * useQueryParamsFilters only touches the query parameters defined in the filters definition when updating the url so you can use multiple useQueryParamsFilters in the same page as long as they have a different set of filters.
 * (possible evolution: pass a "suffix" option to useQueryParamsFilters to be able to use the same filters definitions multiple times in the same page. useQueryParamsFilters({search: {}}, {suffix: '_1'}) would read/write the query param "search_1" instead of "search" so you could . The return value would still manipulates the filters unprefix eg here returnValue.filters.search, not returnValue.filters.search_1.)
 *
 * Note that query parameters are only read on mount so if you update the query parameters yourself (don't) instead of using `setFilter` the filters will not be updated.
 *
 * filter definition can contains the following parameters:
 * - allowed: list(string), optional: list of allowed representations. If the query parameter value does not match any allowed representation the default value will be used instead.
 * - default: string, optional. Default value of the filter, `undefined` if not set. On init the filter the filter will take this value if . Note that interactions between `default` and `toInternalValue` / `toRepresentation` are not correctly defined for now so don't use both in the same filter.
 * - toInternalValue: (string?) => any?: called to convert from the query parameters value (called "representation") to the internal value. For example you might want to manipulate. The argument is already url-decoded. If not set `id` is used (`x => x`). Invariant: Object.is(internalValue, toInternalValue(toRepresentation(internalValue)))
 * - toRepresentation: (any?) => string?: called to convert from the internval value to the value to be used in query paremeters. If not set `id` is used (`x => x`). Invariant: representation === toRepresentation(toInternalValue(representation))
 *
 * toRepresentation and toInternalValue will be called even if the value or representation (respectively) are falsy (null, undefined, empty string, false…). i.e. even if the filter was not found in the query parameters.
 *
 * example:
 *  const filtersDefinitions = {
   	status: {
   		allowed: [CONSTANTS.ARCHIVED, CONSTANTS.NOT_ARCHIVED, CONSTANTS.ON, CONSTANTS.OFF],
   		default: CONSTANTS.NOT_ARCHIVED
   	},
   	created: {
   		allowed: [CONSTANTS.SIX_MONTHS, CONSTANTS.TWELVE_MONTHS]
   	},
   	ordering: {
   		allowed: ['name-asc', 'name-desc'],
   		toInternalValue(representation) {
   			if (representation) {
   				const [column, direction] = representation.split('-');
   				return {column, direction};
   			} else {
   				return {column: null, direction: null};
   			}
   		},
   		toRepresentation(value) {
   			if (value.column && value.direction) {
   				return `${value.column}-${value.direction}`;
   			} else {
   				return null;
   			}
   		}
   	},
   	search: {
   		toInternalValue(representation) {
   			return representation ?? "";
   		}
   	}
   };
 *
 * const { filters, setFilter } = useQueryParamsFilters(filtersDefinition);
 * +------------------------------+-------------------------------------------------------------------------------------------------------------+
 * | query params                 |  filters                                                                                                    |
 * +------------------------------+-------------------------------------------------------------------------------------------------------------+
 * |                              | { status: "not_archived", created: undefined, ordering: { column: null, direction: null }, search: "" }     |
 * | ?unrelated=1                 | { status: "not_archived", created: undefined, ordering: { column: null, direction: null }, search: "" }     |
 * | ?status=*                    | { status: undefined, created: undefined, ordering: { column: null, direction: null }, search: "" }          |
 * | ?status=nope                 | { status: "not_archived", created: undefined, ordering: { column: null, direction: null }, search: "" }     |
 * | ?ordering=name-desc          | { status: "not_archived", created: undefined, ordering: { column: "name", direction: "desc" }, search: "" } |
 * | ?created=6&ordering=badvalue | { status: "not_archived", created: "6", ordering: { column: null, direction: null }, search: "" }           |
 * +------------------------------+-------------------------------------------------------------------------------------------------------------+
 */
export default function useQueryParamsFilters(filtersDefinitions) {
	const location = useLocation();
	const history = useHistory();

	const [filters, setFilters] = React.useState(() => {
		const searchParams = new URLSearchParams(location.search.substring(1)); // remove the first char because it's the path / query params separator ("?")
		const filters = {};
		for (let filter in filtersDefinitions) {
			filters[filter] = getFilterInternalValue(
				searchParams,
				filter,
				filtersDefinitions[filter]
			);
		}
		return filters;
	});

	return {
		filters,
		setFilter: (filter, value) => {
			const newFilters = {
				...filters,
				[filter]: value
			};
			setFilters(newFilters);

			// note: this "update url" step should theoretically be done in a useEffect
			// but the only possible filter with a "fast" changing value is the search
			// and the search input is debounced
			// so to make our lives easier no useEffect (because it would also mean having a ref to the current filter values, bleh)
			const outSearchParams = new URLSearchParams(
				location.search.substring(1)
			);
			for (let filter in filtersDefinitions) {
				updateFilterRepresentation(
					outSearchParams,
					filter,
					filtersDefinitions[filter],
					newFilters[filter]
				);
			}
			history.replace(
				`${location.pathname}?${outSearchParams.toString()}${
					location.hash
				}`
			);
		}
	};
}
